"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { EASE } from "@/lib/motion";
import { explorerTxUrl } from "@/lib/explorers";
import { shortenAddress } from "@/lib/format/amount";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { Button } from "@/components/ui/primitives";

/* The kit's Verify On-Chain modal, behind every hash in the live feeds: a
   centred title and one line of why, then a row per recorded transaction with
   the hash, an explorer link and a copy control, and a Close.

   The kit's frame labels each row with a type and an amount; the public API
   states neither — a bet carries `hashes: string[]` and nothing per hash — so
   the rows show exactly what the platform said, the same reduction the
   whitelabel makes. The explorer link only renders on a chain the table
   knows: a copyable hash with no link beats a guessed URL. */

export type VerifySubject = { hashes: string[]; chainId?: number };

export function VerifyOnChainModal({
  subject,
  onClose,
}: {
  subject: VerifySubject | null;
  onClose: () => void;
}) {
  const panel = useModalChrome(Boolean(subject), onClose);

  return (
    <AnimatePresence>
      {subject && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Verify on-chain"
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="modal-shadow relative flex max-h-[90vh] w-full max-w-[440px] flex-col gap-[16px] overflow-y-auto rounded-2xl border border-border-input bg-elevation-1 p-[24px]"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close verification"
              className="absolute right-[16px] top-[16px] grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
            >
              <Icon name="close" size={18} />
            </button>

            <div className="flex flex-col items-center gap-[8px] text-center">
              <h2 className="text-[17.5px] font-bold leading-[1.4] text-fg">Verify On-Chain</h2>
              <p className="max-w-[40ch] text-[12px] leading-[1.6] text-fg-subtle">
                The on-chain transactions recorded for this bet. Open any of them in the block
                explorer to verify it independently.
              </p>
            </div>

            <ul className="flex flex-col gap-[8px]">
              {subject.hashes.map((hash) => (
                <HashRow key={hash} hash={hash} chainId={subject.chainId} />
              ))}
            </ul>

            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function HashRow({ hash, chainId }: { hash: string; chainId?: number }) {
  const [copied, setCopied] = useState(false);
  const href = explorerTxUrl(chainId, hash);

  const copy = async () => {
    await navigator.clipboard.writeText(hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <li className="flex items-center gap-[8px] rounded-lg bg-elevation-2 p-[12px]">
      <span className="min-w-0 flex-1 truncate text-[12px] font-bold leading-[1.6] tabular-nums text-brand-fg-accent">
        {shortenAddress(hash, 10, 8)}
      </span>

      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Open in block explorer"
          className="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
        >
          <Icon name="external" size={16} />
        </a>
      )}

      <button
        type="button"
        onClick={() => void copy()}
        aria-label="Copy transaction hash"
        className="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
      >
        <Icon name={copied ? "check" : "copy"} size={16} />
      </button>
    </li>
  );
}
